/**
 * External Imports
 */
import { FC, useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import { BlueChip } from '../../../shared/chips/blueChip';
import { Dropdown } from '../../../shared/dropdowns';

import { RootState } from '../../../../redux/store';
import { clearPatientSearch } from '../../../../redux/actions';

import { useDebounceValue } from '../../../../utils/debouncers';
import { compareObjByStringKey } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Patient } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import { ChipWrapper } from '../../feedbackModal/FeedbackModal.styles';
import { InputHeading, StyledDivider } from './PatientSearch.styles';
import { useLazyGetSearchPatientRoundHistoryQuery } from '../../../../redux/apis/Reports.api';

interface PatientSearchProps {
  authorizedUser: AuthUserProps;
  chipVal?: string;
  showLabel?: boolean; // true by default
  // TODO: this currently is patient full name isnt gonna work due to multiple names
  val: string | string[];
  handleRemovePatient: (data: string) => void;
  setPatientSelection: (patient: Patient) => void;
}

const PatientSearch: FC<PatientSearchProps> = ({
  authorizedUser,
  chipVal,
  showLabel,
  val,
  handleRemovePatient,
  setPatientSelection
}) => {
  const dispatch = useDispatch();

  const [activeSearch, setActiveSearch] = useState('');
  const [query, setQuery] = useState('');
  const [isPatientSearchOpen, setIsPatientSearchOpen] =
    useState<boolean>(false);
  const [showLabelState] = useState<boolean>(showLabel !== false);
  const [getSearchedPatientHistory, { data: searchedPatients }] =
    useLazyGetSearchPatientRoundHistoryQuery();

  const dbPatientSearchTerm = useDebounceValue(query, 250);

  useEffect(() => {
    if (dbPatientSearchTerm.length > 2) {
      getSearchedPatientHistory({
        facilityId: authorizedUser.facilityId,
        patientSearch: dbPatientSearchTerm
      });
    }
  }, [dbPatientSearchTerm]);

  const handleSearching = (e: CustomEvent<any>) => {
    const target = e.target as HTMLInputElement;
    setQuery(target.value);
  };

  const selectPatient = (patient: Patient) => {
    // using function passed as prop to set patient
    setPatientSelection(patient);
    setActiveSearch('');
    setQuery('');
    dispatch(clearPatientSearch());
  };

  return (
    <div id="Modal-Patient-Search">
      {showLabelState ? <InputHeading>Patient</InputHeading> : null}
      {chipVal ? (
        <ChipWrapper onClick={() => handleRemovePatient('patient')}>
          <BlueChip key={chipVal} name={chipVal} />
        </ChipWrapper>
      ) : (
        <div className="position-relative">
          <Dropdown
            id="Modal-Patient-Search-Dropdown"
            focused={activeSearch === 'patient'}
            name="patient"
            open={isPatientSearchOpen}
            placeholder="Start typing name or MRN"
            query={query}
            typeAhead
            setOpen={setIsPatientSearchOpen}
            setTypeAhead={setActiveSearch}
            setTypeAheadChange={handleSearching}
          >
            {[...(searchedPatients || [])]
              .sort(compareObjByStringKey('lastName'))
              .sort(compareObjByStringKey('firstName'))
              .map((patient: Patient) => (
                <div key={`${patient.accountNumber}:${patient.mrn}`}>
                  <div
                    id={`Modal-Patient-Search-Dropdown-${patient.accountNumber}:${patient.mrn}`}
                    aria-selected={val.includes(
                      `${patient.firstname} ${patient.lastname}`
                    )}
                    className={`dropdown-hover-item${
                      val.includes(`${patient.firstname} ${patient.lastname}`)
                        ? ' selected'
                        : ''
                    }`}
                    role="option"
                    tabIndex={0}
                    onClick={() => selectPatient(patient)}
                    onKeyDown={() => selectPatient(patient)}
                  >
                    <p className="mt-1 mb-0">
                      {patient.firstname} {patient.lastname}
                    </p>
                    <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                      {patient.mrn}
                    </p>
                  </div>
                  {searchedPatients?.length > 1 && (
                    <StyledDivider className="my-0" />
                  )}
                </div>
              ))}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser
  };
};

export default connect(mapReduxStateToProps)(PatientSearch);
